import React, {
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
    useMemo,
    useCallback,
} from "react";
import "../css/Map.css";
import axios from "axios";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import * as topojson from "topojson-client";
import { FixedSizeList, areEqual } from "react-window";
import SlidingRadioTabs from "./SlidingRadioTabs";
// 서버 연동 방식(보존)
// import { getTableNameFromFileNameWithoutExt } from "../utils/mapDataTableMapping";
// 서버 연동 방식(보존): 요청에 따라 삭제하지 않고 주석 처리
// import {
//     fetchMapDataInBounds,
//     boundsToApiParams,
//     transformApiDataToFrontendFormat,
//     fetchBoundaryList,
//     fetchBoundaryGeoJson,
//     fetchDataFileList,
// } from "../services/mapDataApi";

// 디바운스 속도 설정 (조절 가능)
const DEBOUNCE_CONFIG = {
    BOUNDS_CHANGED: 100,    // 지도 이동 시 디바운스 (ms)
    ZOOM_CHANGED: 100,      // 줌 변경 시 디바운스 (ms)
    DATA_RENDERING: 100,    // 데이터 렌더링 시 디바운스 (ms)
};
// 이 개수 초과 시 regionList를 청크로 비동기 구축
const LARGE_BOUNDARY_THRESHOLD = 1500;
const REGION_LIST_CHUNK_SIZE = 500;
// 경계 체크박스: React.memo로 불필요한 재렌더 방지 (법정동/행정동 수천 개 체크 시 멈춤 방지)
const BoundaryCheckboxRow = React.memo(({ itemKey, checked, onChange, name, isSearchResult, className }) => {
    return (
        <label 
            className={className || ''} 
            style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                cursor: 'pointer', 
                padding: '4px 20px', // 좌우여백 넓히기
                boxSizing: 'border-box', // 패딩 늘려도 줄이 안 깨지게 하는 안전장치
                minHeight: '24px'
            }}
        >
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => {
                    onChange(itemKey, e.target.checked);
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                style={{ 
                    flexShrink: 0, 
                    marginRight: '4px', // 체크박스와 글자 사이 간격
                    marginTop: '2px',
                    cursor: 'pointer',
                    width: '14px',
                    height: '14px'
                }}
            />
            <span style={{ 
                flex: 1,
                fontSize: '13px',
                lineHeight: '1.3',
                wordBreak: 'keep-all',
                overflowWrap: 'break-word'
            }}>
                {name ?? ''}
            </span>
        </label>
    );
}, (prevProps, nextProps) => {
    // checked, name, itemKey가 같으면 재렌더 skip
    return prevProps.checked === nextProps.checked && prevProps.name === nextProps.name && prevProps.itemKey === nextProps.itemKey && prevProps.isSearchResult === nextProps.isSearchResult;
});

//가상 스크롤용 2열 행 컴포넌트
const TwoColumnVirtualRow = React.memo(({ index, style, data }) => {
    // data로 전달받은 props 꺼내기
    const { 
        fullList, 
        boundaryCheckedSet, 
        handleBoundaryToggle, 
        boundarySearchKeyword,
        boundaryId  // boundaryId 추가 (itemKey 생성에 필요)
    } = data;

    // 2열 인덱스 계산
    const firstIndex = index * 2;
    const secondIndex = index * 2 + 1;
    const item1 = fullList[firstIndex];
    const item2 = fullList[secondIndex];

    // 공통 렌더링 함수
    const renderItem = (item) => {
        if (!item) return null;
        
        // 검색어 하이라이트 여부
        const isSearchResult = boundarySearchKeyword && 
            item.name?.toLowerCase().includes(boundarySearchKeyword.toLowerCase());
        
        // 고유 키 생성 (boundaryId 포함)
        const itemKey = `boundary:${boundaryId}:${item.id}`;

        return (
            <div style={{ flex: 1, minWidth: 0 }}> {/* 좌우 아이템 간격 조절 */}
                <BoundaryCheckboxRow
                    itemKey={itemKey}
                    checked={boundaryCheckedSet.has(itemKey)}
                    onChange={handleBoundaryToggle}
                    name={item.name ?? `영역 ${item.id}`}
                    isSearchResult={isSearchResult}
                    className={`informationCheck ${isSearchResult ? 'searchResult' : ''}`}
                    // BoundaryCheckboxRow 내부 스타일 덮어쓰기 (꽉 차게)
                    style={{ width: '100%', height: '100%' }} 
                />
            </div>
        );
    };

    return (
        <div style={{ 
            ...style, 
            display: 'flex', 
            width: '100%', 
            // padding: '0 20px', // 전체 리스트 좌우 여백 (여기서 조절!)
            boxSizing: 'border-box',
            alignItems: 'center'
        }}>
            {/* 왼쪽 아이템 */}
            {renderItem(item1)}
            
            {/* 오른쪽 아이템 (있을 때만) */}
            {item2 ? renderItem(item2) : <div style={{ flex: 1 }} />}
        </div>
    );
}, areEqual);

// ⭐ Boundary 섹션의 콘텐츠를 렌더링하는 컴포넌트 (useMemo 사용을 위해 분리)
const BoundarySectionContent = React.memo(({ 
    fullList, 
    boundary, 
    boundaryCheckedSet, 
    handleBoundaryToggle, 
    boundarySearchKeyword 
}) => {
    // ⭐ itemData 최적화 (이 객체가 변하면 리스트가 깜빡임)
    const itemData = useMemo(() => ({
        fullList,
        boundaryCheckedSet,
        handleBoundaryToggle,
        boundarySearchKeyword,
        boundaryId: boundary.id  // boundaryId 포함
    }), [fullList, boundaryCheckedSet, handleBoundaryToggle, boundarySearchKeyword, boundary.id]);
    
    // 대량 리스트(1500개 이상)는 가상 스크롤 (2열 레이아웃)
    if (fullList.length > LARGE_BOUNDARY_THRESHOLD) {
        return (
            <FixedSizeList
                height={400} // 리스트 전체 높이
                itemCount={Math.ceil(fullList.length / 2)} // ⭐ 전체 개수 나누기 2 (올림)
                itemSize={45} // ⭐ 한 줄 높이 (50 -> 45)
                width="210%"
                itemData={itemData} // 데이터 전달
                // style={{ overflowX: 'hidden'}} // 가로스크롤 숨기기기
            >
                {TwoColumnVirtualRow}
            </FixedSizeList>
        );
    }
    
    // 소량 리스트는 일반 렌더링
    return (
        <>
            {fullList.map((regionObject) => {
                const itemKey = `boundary:${boundary.id}:${regionObject.id}`;
                const isSearchResult = boundarySearchKeyword && regionObject.name?.toLowerCase().includes(boundarySearchKeyword.toLowerCase());
                return (
                    <BoundaryCheckboxRow
                        key={regionObject.id}
                        itemKey={itemKey}
                        checked={boundaryCheckedSet.has(itemKey)}
                        onChange={handleBoundaryToggle}
                        name={regionObject.name ?? `영역 ${regionObject.id}`}
                        isSearchResult={isSearchResult}
                        className={`informationCheck ${isSearchResult ? 'searchResult' : ''}`}
                    />
                );
            })}
        </>
    );
});

const Map = ({ content }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [dockOpen, setDockOpen] = useState(true);
    
    // 파일 목록 상태 (API에서 데이터를 가져올 때 사용)
    const [dataFileList, setDataFileList] = useState([]); // 파일 목록 (예: ["골프장.csv", "병원.csv"])
    const [isDataLoading, setIsDataLoading] = useState(true); // 데이터 로딩 상태
    
    // 서버 API에서 가져온 데이터 상태 (지도 범위 변경 시 동적으로 로드)
    // 구조: { fileName: { bounds: {...}, data: { regionName: [...] } } }
    const [apiDataCache, setApiDataCache] = useState({});
    
    // 주소에서 행정구역명 추출하는 함수
    const extractRegionFromAddress = (address) => {
        if (!address) return '기타';
        
        const validRegions = [
            '서울특별시',
            '부산광역시',
            '대구광역시',
            '인천광역시',
            '광주광역시',
            '대전광역시',
            '울산광역시',
            '세종특별자치시',
            '경기도',
            '강원특별자치도',
            '충청북도',
            '충청남도',
            '전북특별자치도',
            '전라남도',
            '경상북도',
            '경상남도',
            '제주특별자치도'
        ];
        
        let cityName = '';
        
        if (address.includes('특별자치도')) {
            const match = address.match(/([가-힣]+특별자치도)/);
            cityName = match ? match[1] : '';
        } else if (address.includes('특별자치시')) {
            const match = address.match(/([가-힣]+특별자치시)/);
            cityName = match ? match[1] : '';
        } else if (address.includes('광역시')) {
            const match = address.match(/([가-힣]+광역시)/);
            cityName = match ? match[1] : '';
        } else if (address.includes('특별시')) {
            const match = address.match(/([가-힣]+특별시)/);
            cityName = match ? match[1] : '';
        } else if (address.includes('도')) {
            const match = address.match(/([가-힣]+도)/);
            cityName = match ? match[1] : '';
        }
        
        return (cityName && validRegions.includes(cityName)) ? cityName : '기타';
    };

    // API/로컬 데이터를 행정구역별로 분류하는 함수
    const categorizeDataByRegion = (data) => {
        const categorized = {};
        
        data.forEach((item) => {
            const region = extractRegionFromAddress(item.address || '');
            if (!categorized[region]) {
                categorized[region] = [];
            }
            categorized[region].push(item);
        });
        
        return categorized;
    };

    // CSV 한 줄 파싱(따옴표/쉼표 처리)
    const parseCsvLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === "," && !inQuotes) {
                result.push(current.trim());
                current = "";
            } else {
                current += ch;
            }
        }
        result.push(current.trim());
        return result;
    };

    // 로컬 CSV 텍스트를 앱 내부 공통 포맷으로 변환
    const parseLocalCsvToItems = (csvText) => {
        const lines = csvText.split(/\r?\n/).filter((line) => line.trim().length > 0);
        if (lines.length <= 1) return [];

        const headers = parseCsvLine(lines[0]);
        const headerIndex = {
            dataName: headers.findIndex((h) => h === "데이터명"),
            lat: headers.findIndex((h) => h === "위도"),
            lng: headers.findIndex((h) => h === "경도"),
            address: headers.findIndex((h) => h === "주소"),
            dataValue: headers.findIndex((h) => h === "데이터값"),
            detail: headers.findIndex((h) => h === "세부사항"),
            collectedAt: headers.findIndex((h) => h === "수집날짜"),
            source: headers.findIndex((h) => h === "출처"),
        };

        const items = [];
        for (let i = 1; i < lines.length; i++) {
            const cols = parseCsvLine(lines[i]);
            const lat = parseFloat(cols[headerIndex.lat] ?? "");
            const lng = parseFloat(cols[headerIndex.lng] ?? "");
            if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

            items.push({
                id: i,
                data_name: cols[headerIndex.dataName] ?? "",
                lat,
                lng,
                address: cols[headerIndex.address] ?? "",
                data_value: cols[headerIndex.dataValue] ?? "",
                detail: cols[headerIndex.detail] ?? "",
                collected_at: cols[headerIndex.collectedAt] ?? "",
                source: cols[headerIndex.source] ?? "",
                name: cols[headerIndex.dataName] ?? "",
                weight: cols[headerIndex.dataValue] ?? "",
            });
        }
        return items;
    };

    // Map copy.js에서 가져온 spatialInfoOptions 데이터 구조 - useMemo로 다국어 지원
    const spatialInfoOptions = useMemo(() => {
        const options = [];
        
        // 파일 목록을 기반으로 옵션 생성 (실제 데이터는 API에서 동적으로 로드)
        if (dataFileList.length > 0) {
            dataFileList.forEach((file) => {
                const fileName = file.replace('.csv', '');
                const fileId = `newdata_${fileName.replace(/[^a-zA-Z0-9가-힣]/g, '_')}`;
                
                // 행정구역 표준 순서 정의
                const regionOrder = [
                    '서울특별시',
                    '부산광역시',
                    '대구광역시',
                    '인천광역시',
                    '광주광역시',
                    '대전광역시',
                    '울산광역시',
                    '세종특별자치시',
                    '경기도',
                    '강원특별자치도',
                    '충청북도',
                    '충청남도',
                    '전북특별자치도',
                    '전라남도',
                    '경상북도',
                    '경상남도',
                    '제주특별자치도',
                    '기타'
                ];
                
                // API 캐시에서 데이터 가져오기 (항상 전체 시도 목록 표시, 데이터는 캐시에 있으면 채움)
                const cachedData = apiDataCache[fileName];
                const categories = regionOrder.map(cityName => ({
                    id: `${fileId}_${cityName}`,
                    name: cityName,
                    data: (cachedData && cachedData.data && cachedData.data[cityName]) ? cachedData.data[cityName] : []
                }));
                
                options.push({
                    id: fileId,
                    name: fileName,
                    description: `${fileName} 데이터입니다.`,
                    type: "marker",
                    categories: categories
                });
            });
        }
        
        return options;
    }, [content, dataFileList, apiDataCache]);

    const mapRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [searchAddress, setSearchAddress] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [infoIndex, setInfoIndex] = useState(0);
    const [travelMode, setTravelMode] = useState("car");
    const [activeHeatmaps, setActiveHeatmaps] = useState([]);
    const [activeMarkerClusterers, setActiveMarkerClusterers] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState({});
    const [geoJsonData, setGeoJsonData] = useState({});
    const [boundarySourceGeoJsonData, setBoundarySourceGeoJsonData] = useState({});
    const [mobileStage, setMobileStage] = useState("home");
    const [isMobile, setIsMobile] = useState(false);
    
    // 길찾기 관련 상태
    const [directionMode, setDirectionMode] = useState(null);
    const [directionResults, setDirectionResults] = useState([]);
    const [startMarker, setStartMarker] = useState(null);
    const [endMarker, setEndMarker] = useState(null);
    const [startInfo, setStartInfo] = useState(null);
    const [endInfo, setEndInfo] = useState(null);
    const [startAddress, setStartAddress] = useState("");
    const [endAddress, setEndAddress] = useState("");
    const [activeMarkers, setActiveMarkers] = useState([]);
    const [activeLines, setActiveLines] = useState([]);
    const [routeDistance, setRouteDistance] = useState(null);
    const [routeDuration, setRouteDuration] = useState(null);
    const [routePolyline, setRoutePolyline] = useState(null);
    const [routeData, setRouteData] = useState(null);
    const [showDetails, setShowDetails] = useState(true);
    const [transitSteps, setTransitSteps] = useState([]);
    const [carSteps, setCarSteps] = useState([]);
    const [walkSteps, setWalkSteps] = useState([]);

    // Map.js의 UI 상태 관리
    const [activeTab, setActiveTab] = useState(() => content?.mapBoundaryTab || "행정경계");
    const [expandedBoundaries, setExpandedBoundaries] = useState([]);
    const [boundarySearchKeyword, setBoundarySearchKeyword] = useState(""); // 행정구역 검색 키워드
    const [filteredBoundaries, setFilteredBoundaries] = useState({}); // 필터링된 행정구역
    const [boundaryListCache, setBoundaryListCache] = useState({}); // API 목록(id+name)으로 체크박스 빠르게 표시
    const [dataSearchKeyword, setDataSearchKeyword] = useState(""); // 데이터 검색 키워드
    const [filteredSpatialOptions, setFilteredSpatialOptions] = useState([]); // 필터링된 데이터 옵션
    const [boundaryCheckedSet, setBoundaryCheckedSet] = useState(() => new Set());
    
    // 경계 체크박스 토글 핸들러 (useCallback으로 함수 재생성 방지 → 법정동/행정동 수천 개 렌더 시 성능 최적화)
    const handleBoundaryToggle = useCallback((itemKey, checked) => {
        setBoundaryCheckedSet((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(itemKey);
            } else {
                next.delete(itemKey);
            }
            return next;
        });
    }, []);

    // Map copy.js의 공간정보 데이터 상태 관리
    const [openSpatialData, setOpenSpatialData] = useState(() => new Set());
    const [spatialDataState, setSpatialDataState] = useState(() =>
        Object.fromEntries(
            spatialInfoOptions.map((s) => [
                s.id,
                { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
            ])
        )
    );
    const [appliedSpatialDataState, setAppliedSpatialDataState] = useState(() =>
        Object.fromEntries(
            spatialInfoOptions.map((s) => [
                s.id,
                { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
            ])
        )
    );

    // spatialInfoOptions가 변경될 때 상태 초기화 (새로운 옵션 추가 시)
    useEffect(() => {
        if (spatialInfoOptions.length === 0) return;
        
        setSpatialDataState((prev) => {
            const newState = {};
            spatialInfoOptions.forEach((s) => {
                // 기존 상태가 있으면 유지, 없으면 새로 생성
                newState[s.id] = prev[s.id] || { 
                    mode: s.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            });
            return newState;
        });
        setAppliedSpatialDataState((prev) => {
            const newState = {};
            spatialInfoOptions.forEach((s) => {
                // 기존 상태가 있으면 유지, 없으면 새로 생성
                newState[s.id] = prev[s.id] || { 
                    mode: s.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            });
            return newState;
        });
    }, [spatialInfoOptions]);

    // 로컬 mapData JSON으로 경계 로드
    const boundaryOptions = [
        { id: "sido", name: content?.mapBoundarySido || "시도경계", url: `${process.env.PUBLIC_URL}/mapData/korea_sido.json`, tableName: "kr_sido" },
        { id: "sigungu", name: content?.mapBoundarySigungu || "시군구경계", url: `${process.env.PUBLIC_URL}/mapData/korea_sigungu.json`, tableName: "kr_sigungu" },
        { id: "beopjeongdong", name: content?.mapBoundaryBeopjeongdong || "법정동경계", url: `${process.env.PUBLIC_URL}/mapData/korea_beopjeongdong.json`, tableName: "kr_beopjeongdong" },
        { id: "haengjeongdong", name: content?.mapBoundaryHaengjeongdong || "행정동경계", url: `${process.env.PUBLIC_URL}/mapData/korea_haengjeongdong.json`, tableName: "kr_haengjeongdong" },
    ];

    // Map.js의 메뉴 순서 (공간정보 → 길찾기 → 위치검색)
    const infoMenuList = [
        {
            id: "layers",
            title: content?.mapSpatialInfo || "공간정보",
            content: content?.mapSpatialInfoDesc || "공간 관련 정보를 제공합니다.",
        },
        {
            id: "directions", 
            title: content?.mapDirections || "길찾기",
            content: content?.mapDirectionsDesc || "출발지와 목적지를 설정하세요.",
        },
        {
            id: "search",
            title: content?.mapSearch || "위치검색",
            content: content?.mapSearchDesc || "장소를 검색할 수 있습니다.",
        },
    ];

    // Map copy.js의 공간정보 관련 함수들
    const toggleOpenSpatialData = (id) =>
        setOpenSpatialData((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const setSpatialMode = (id, mode) =>
        setSpatialDataState((st) => ({ ...st, [id]: { ...st[id], mode } }));

    const toggleSpatialCategory = (id, categoryId, on) =>
        setSpatialDataState((st) => {
            const next = new Set(st[id].selected);
            on ? next.add(categoryId) : next.delete(categoryId);
            return { ...st, [id]: { ...st[id], selected: next } };
        });

    const spatialAllState = (secId) => {
        const categories = spatialInfoOptions.find((s) => s.id === secId)?.categories || [];
        const total = categories.length;
        
        // 상태가 없거나 categories가 비어있으면 false 반환
        if (!spatialDataState[secId] || total === 0) {
            return {
                all: false,
                some: false,
            };
        }
        
        const count = categories.filter((c) =>
            spatialDataState[secId].selected.has(c.id)
        ).length;
        return {
            all: count === total && total > 0,
            some: count > 0 && count < total,
        };
    };

    const toggleAllSpatialCategories = (secId, on) =>
        setSpatialDataState((st) => {
            const categories = spatialInfoOptions.find((s) => s.id === secId)?.categories || [];
            
            // 상태가 없으면 초기화
            if (!st[secId]) {
                const spatialOption = spatialInfoOptions.find((s) => s.id === secId);
                st[secId] = { 
                    mode: spatialOption?.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            }
            
            return {
                ...st,
                [secId]: { 
                    ...st[secId], 
                    selected: new Set(on ? categories.map(c => c.id) : [])
                },
            };
        });

    // 행정구역 키워드 검색 함수
    const searchBoundaries = (keyword) => {
        if (!keyword.trim()) {
            setFilteredBoundaries({});
            return;
        }

        // boundaryListCache와 레거시 regionList 모두 검색 (API 전환 후 호환)
        const allBoundaries = { ...regionList, ...boundaryListCache };
        if (!allBoundaries || Object.keys(allBoundaries).length === 0) {
            return;
        }

        const filtered = {};
        Object.entries(allBoundaries).forEach(([boundaryId, regions]) => {
            // regions가 배열인지 확인하고 안전하게 처리
            if (Array.isArray(regions)) {
                const matchingRegions = regions.filter(region => 
                    region && region.name && region.name.toLowerCase().includes(keyword.toLowerCase())
                );
                if (matchingRegions.length > 0) {
                    filtered[boundaryId] = matchingRegions;
                }
            }
        });

        setFilteredBoundaries(filtered);

        // 검색 결과가 있을 때만 해당 섹션 펼치고 focus (체크하지 않음)
        const firstBoundaryId = Object.keys(filtered)[0];
        if (firstBoundaryId && filtered[firstBoundaryId] && filtered[firstBoundaryId].length > 0) {
            // 해당 행정구역 섹션을 펼치기만 함 (체크하지 않음)
            setExpandedBoundaries(prev => 
                prev.includes(firstBoundaryId) ? prev : [...prev, firstBoundaryId]
            );
        }
    };

    // 데이터 검색 함수
    const searchData = (keyword) => {
        if (!keyword.trim()) {
            setFilteredSpatialOptions([]);
            return;
        }

        const filtered = spatialInfoOptions.filter(spatial => 
            spatial.name.toLowerCase().includes(keyword.toLowerCase()) ||
            spatial.description.toLowerCase().includes(keyword.toLowerCase()) ||
            spatial.categories.some(category => 
                category.name.toLowerCase().includes(keyword.toLowerCase())
            )
        );

        setFilteredSpatialOptions(filtered);

        // 검색 결과가 있을 때만 해당 섹션 펼치기
        if (filtered.length > 0) {
            filtered.forEach(spatial => {
                if (!openSpatialData.has(spatial.id)) {
                    setOpenSpatialData(prev => new Set([...prev, spatial.id]));
                }
            });
        }
    };
    useEffect(() => {
        if (!mapInstance || !routeData) return;

        const { path, distance, duration, steps = [] } = routeData;

        setRouteDistance(distance);
        setRouteDuration(duration);

        if (routePolyline) routePolyline.forEach((line) => line.setMap(null));
        activeLines.forEach((line) => line.setMap(null));
        activeMarkers.forEach((marker) => marker.setMap(null));
        setRoutePolyline(null);
        setActiveLines([]);
        setActiveMarkers([]);
        setTransitSteps([]);
        setCarSteps([]);
        setWalkSteps([]);

        const bounds = new window.google.maps.LatLngBounds();

        if (travelMode === "transit" && steps) {
            // Use travelMode from state
            const newActiveLines = [];
            const newActiveMarkers = [];

            steps.forEach((step, index) => {
                let strokeColor;
                let strokeWeight = 5;
                let icons = [];

                if (step.travel_mode === "WALKING") {
                    strokeColor = "#FBAF32";
                    strokeWeight = 5;
                    icons = [
                        {
                            icon: {
                                path: "M 0,-1 0,1",
                                strokeOpacity: 1,
                                scale: 3,
                            },
                            offset: "0",
                            repeat: "15px",
                        },
                    ];
                } else if (step.travel_mode === "TRANSIT") {
                    if (
                        step.transit_details &&
                        step.transit_details.vehicle_type === "SUBWAY"
                    ) {
                        strokeColor = "#3070b1";
                    } else if (
                        step.transit_details &&
                        step.transit_details.vehicle_type === "BUS"
                    ) {
                        strokeColor = "#5ABF41";
                    } else {
                        strokeColor = "#6f42c1";
                    }
                } else {
                    strokeColor = "#FBAF32";
                }

                if (step.polyline) {
                    const decoded =
                        window.google.maps.geometry.encoding.decodePath(
                            step.polyline
                        );

                    const borderLine = new window.google.maps.Polyline({
                        path: decoded,
                        strokeColor: "#424242ff",
                        strokeOpacity: 1,
                        strokeWeight: (strokeWeight || 5) + 3,
                        map: mapInstance,
                        zIndex: 1,
                    });
                    newActiveLines.push(borderLine);
                    const innerLine = new window.google.maps.Polyline({
                        path: decoded,
                        strokeColor,
                        strokeOpacity: 0.8,
                        strokeWeight,
                        map: mapInstance,
                        icons,
                        zIndex: 2,
                    });
                    newActiveLines.push(innerLine);

                    const decodedStepPath =
                        window.google.maps.geometry.encoding.decodePath(
                            step.polyline
                        );

                    if (decodedStepPath.length > 0) {
                        const stepStartLocation = decodedStepPath[0];

                        const stepMarker = new window.google.maps.Marker({
                            position: stepStartLocation,
                            map: mapInstance,
                            label: {
                                text: `${index + 1}`,
                                color: "white",
                                fontWeight: "bold",
                            },
                            icon: {
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 10,
                                fillColor: strokeColor,
                                fillOpacity: 1,
                                strokeWeight: 1,
                                strokeColor: "#fff",
                            },
                            zIndex: 3,
                        });

                        newActiveMarkers.push(stepMarker);
                    }
                }
            });

            const transformedSteps = transformSteps(steps);
            setTransitSteps(transformedSteps);
            setActiveLines(newActiveLines);
            setActiveMarkers((prev) => [...prev, ...newActiveMarkers]);

            const decodedPathForBounds =
                window.google.maps.geometry.encoding.decodePath(path);
            decodedPathForBounds.forEach((p) => {
                const lat = p.lat();
                const lng = p.lng();
                if (
                    typeof lat === "number" &&
                    typeof lng === "number" &&
                    !isNaN(lat) &&
                    !isNaN(lng)
                ) {
                    bounds.extend(new window.google.maps.LatLng(lat, lng));
                } else {
                    console.warn(
                        `[Map.js] Invalid coordinate in decoded path (transit):`,
                        p
                    );
                }
            });
        } else {
            // 자동차 또는 도보
            const strokeColor = travelMode === "walk" ? "#FBAF32" : "#FBAF32";
            const borderColor =
                travelMode === "walk" ? "#424242ff" : "#424242ff";
            const outerLine = new window.google.maps.Polyline({
                path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
                strokeColor: borderColor,
                strokeOpacity: 1,
                strokeWeight: 8, // 메인 라인보다 두꺼움
                map: mapInstance,
                zIndex: 1,
            });

            // 안쪽 메인 라인
            const innerLine = new window.google.maps.Polyline({
                path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
                strokeColor: strokeColor,
                strokeOpacity: 1,
                strokeWeight: 6, // 테두리보다 살짝 얇음
                map: mapInstance,
                zIndex: 2,
            });

            setRoutePolyline([outerLine, innerLine]);
            if (travelMode === "car") {
                const mergedSteps = mergeTmapCarSteps(steps);
                setCarSteps(mergedSteps);
            } else if (travelMode === "walk") {
                const mergedSteps = mergeTmapCarSteps(steps);
                setWalkSteps(mergedSteps);
            }
            path.forEach((p) => {
                if (
                    typeof p.lat === "number" &&
                    typeof p.lng === "number" &&
                    !isNaN(p.lat) &&
                    !isNaN(p.lng)
                ) {
                    bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
                } else {
                    console.warn(
                        `[Map.js] Invalid coordinate in path (car/walk):`,
                        p
                    );
                }
            });
        }

        mapInstance.fitBounds(bounds);
    }, [mapInstance, travelMode, routeData]);
    // 초기화 및 이벤트 핸들러들
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const sync = () => setIsMobile(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (isMobile) setIsPanelOpen(mobileStage !== "home");
    }, [isMobile, mobileStage]);

    // 경계 feature에서 식별자(ID) 추출 - 시도/시군구/법정동/행정동 등 다양한 속성 키 지원
    const getBoundaryFeatureId = (properties) => {
        if (!properties) return null;
        return (
            properties.EMD_CD || properties.emd_cd ||
            properties.BJCD || properties.bjcd ||
            properties.ADM_CD || properties.adm_cd ||
            properties.SIDO_CD || properties.sido_cd ||
            properties.code || properties.COD ||
            properties.id
        ) || null;
    };

    // 경계 feature에서 이름 추출
    const getBoundaryFeatureName = (properties) => {
        if (!properties) return null;
        return (
            properties.EMD_NM || properties.emd_nm ||
            properties.ADM_NM || properties.adm_nm ||
            properties.SIDO_NM || properties.sido_nm ||
            properties.NAME || properties.name
        ) || null;
    };

    // 대량 경계(법정동/행정동)용 regionList - 청크로 비동기 구축해 멈춤 방지
    const [largeRegionLists, setLargeRegionLists] = useState({});

    // 지역 데이터 처리 - useMemo로 최적화 (소량은 동기, 대량은 largeRegionLists 사용)
    const regionList = useMemo(() => {
        const sourceGeo = Object.keys(boundarySourceGeoJsonData).length > 0 ? boundarySourceGeoJsonData : geoJsonData;
        if (Object.keys(sourceGeo).length === 0) {
            return {};
        }
        const newRegionList = {};
        for (const boundaryId in sourceGeo) {
            const data = sourceGeo[boundaryId];
            if (!data || !data.features) continue;
            const features = data.features;
            if (features.length <= LARGE_BOUNDARY_THRESHOLD) {
                const allRegions = features
                    .map((feature) => {
                        const id = getBoundaryFeatureId(feature.properties) ?? (feature.id != null ? String(feature.id) : null);
                        const name = getBoundaryFeatureName(feature.properties) ?? (feature.properties?.name) ?? (feature.id != null ? `영역 ${feature.id}` : null);
                        return name && id ? { id, name } : null;
                    })
                    .filter(Boolean);
                newRegionList[boundaryId] = allRegions;
            } else {
                newRegionList[boundaryId] = largeRegionLists[boundaryId] ?? [];
            }
        }
        return newRegionList;
    }, [geoJsonData, boundarySourceGeoJsonData, largeRegionLists]);

    // 대량 feature 경계는 청크로 regionList 구축 (멈춤 방지)
    useEffect(() => {
        for (const boundaryId in geoJsonData) {
            const data = geoJsonData[boundaryId];
            if (!data || !data.features || data.features.length <= LARGE_BOUNDARY_THRESHOLD) continue;
            const features = data.features;
            const current = largeRegionLists[boundaryId];
            if (current && current.length === features.length) continue;

            let result = [];
            let index = 0;
            const buildChunk = () => {
                const end = Math.min(index + REGION_LIST_CHUNK_SIZE, features.length);
                for (let i = index; i < end; i++) {
                    const feature = features[i];
                    const id = getBoundaryFeatureId(feature.properties) ?? (feature.id != null ? String(feature.id) : null);
                    const name = getBoundaryFeatureName(feature.properties) ?? (feature.properties?.name) ?? (feature.id != null ? `영역 ${feature.id}` : null);
                    if (name && id) result.push({ id, name });
                }
                index = end;
                setLargeRegionLists((prev) => ({ ...prev, [boundaryId]: [...result] }));
                if (index < features.length) requestAnimationFrame(buildChunk);
            };
            requestAnimationFrame(buildChunk);
        }
    }, [geoJsonData]);

    // 선택된 지역 렌더링 - 청크 단위로 추가해 멈춤 방지
    const MAP_FEATURE_CHUNK_SIZE = 200;
    useEffect(() => {
        if (!mapInstance) return;

        const timer = setTimeout(() => {
            const regions = selectedRegions;
            const geo = geoJsonData;
            
            console.log('🗺️ 경계 렌더링 useEffect 트리거');
            console.log('selectedRegions:', regions);
            console.log('geoJsonData keys:', Object.keys(geo));

            // ⭐ 조기 반환: 선택된 지역이 없으면 지도만 비우고 종료 (성능 최적화)
            if (Object.keys(regions).length === 0 || Object.keys(geo).length === 0) {
                console.log('선택된 지역 없음 - 지도 비우기');
                requestAnimationFrame(() => {
                    const toRemove = [];
                    mapInstance.data.forEach((feature) => toRemove.push(feature));
                    toRemove.forEach((feature) => mapInstance.data.remove(feature));
                });
                return;
            }

            requestAnimationFrame(() => {
                const toRemove = [];
                mapInstance.data.forEach((feature) => toRemove.push(feature));
                toRemove.forEach((feature) => mapInstance.data.remove(feature));

                const allFeaturesToShow = [];
                Object.entries(geo).forEach(([boundaryId, data]) => {
                    console.log(`처리 중: ${boundaryId}, features: ${data?.features?.length}, selectedRegions: ${regions[boundaryId]?.length}`);
                    if (!data || !data.features) return;
                    const regionsForBoundary = regions[boundaryId] || [];
                    // ID 비교 시 타입 통일 (서버는 숫자/문자 혼용 가능)
                    const regionSet = regionsForBoundary.length > 0
                        ? new Set(regionsForBoundary.map((id) => String(id)))
                        : null;
                    
                    const features =
                        regionSet && regionSet.size > 0
                            ? data.features.filter((feature) => {
                                  const featureId = getBoundaryFeatureId(feature.properties) ?? (feature.id != null ? String(feature.id) : null);
                                  const idStr = featureId != null ? String(featureId) : null;
                                  return idStr && regionSet.has(idStr);
                              })
                            : [];
                    allFeaturesToShow.push(...features);
                });

                const selectedIds = new Set(Object.values(regions).flat());
                const addNextChunk = (start) => {
                    const end = Math.min(start + MAP_FEATURE_CHUNK_SIZE, allFeaturesToShow.length);
                    if (start < end) {
                        mapInstance.data.addGeoJson({
                            type: "FeatureCollection",
                            features: allFeaturesToShow.slice(start, end),
                        });
                    }
                    if (end < allFeaturesToShow.length) {
                        requestAnimationFrame(() => addNextChunk(end));
                    } else {
                        mapInstance.data.setStyle((feature) => { // 경계 스타일 설정
                            const featureId = feature.getProperty("EMD_CD") || feature.getProperty("BJCD") ||
                                feature.getProperty("ADM_CD") || feature.getProperty("SIDO_CD") ||
                                feature.getProperty("code") || feature.getProperty("id");
                            const isSelected = featureId && selectedIds.has(featureId);
                            return {
                                fillColor: isSelected ? "#FF4500" : "#4CAF50",
                                fillOpacity: 0.15, // 경계를 더 투명하게 하여 heatmap과 marker가 위에 잘 보이도록 함
                                strokeColor: "#FFF",
                                strokeWeight: 1,
                            };
                        });
                        const bounds = new window.google.maps.LatLngBounds();
                        allFeaturesToShow.forEach((feature) => {
                            const process = (coords) => {
                                if (coords[0] && Array.isArray(coords[0][0][0])) {
                                    coords.forEach((subCoords) => process(subCoords));
                                } else {
                                    coords.forEach((path) => {
                                        path.forEach((p) =>
                                            bounds.extend(new window.google.maps.LatLng(p[1], p[0]))
                                        );
                                    });
                                }
                            };
                            if (feature.geometry) process(feature.geometry.coordinates);
                        });
                        if (bounds.isEmpty() === false) mapInstance.fitBounds(bounds);
                    }
                };
                addNextChunk(0);
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [mapInstance, selectedRegions, geoJsonData]);

    useEffect(() => {
        // Google Maps API가 이미 로드되어 있는지 확인
        if (window.google && window.google.maps) {
            // 이미 로드되어 있으면 바로 지도 초기화
            initMap();
            return;
        }

        // 이미 스크립트 태그가 있는지 확인
        const existingScript = document.querySelector(
            `script[src*="maps.googleapis.com/maps/api/js"]`
        );
        
        if (existingScript) {
            // 스크립트가 이미 있으면 로드 완료를 기다림
            if (window.google && window.google.maps) {
                initMap();
            } else {
                existingScript.addEventListener('load', () => {
                    initMap();
                });
            }
            return;
        }

        // 스크립트가 없으면 새로 생성하여 추가
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places,marker,geometry,visualization`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            initMap();
        };
        script.onerror = (e) => {
            console.error("Failed to load Google Maps script:", e);
        };
        document.head.appendChild(script);
    }, []);

    // 파일 목록은 로컬 public/mapData에서 로드
    useEffect(() => {
        setIsDataLoading(true);
        const loadFileList = async () => {
            try {
                // 서버 연동 방식(보존)
                // const fileList = await fetchDataFileList();
                // if (Array.isArray(fileList) && fileList.length > 0) {
                //     setDataFileList(fileList);
                // } else {
                //     const fallback = await fetch(`${process.env.PUBLIC_URL}/mapData/newDataFileList.json`).then(r => r.ok ? r.json() : []).catch(() => []);
                //     setDataFileList(Array.isArray(fallback) ? fallback : []);
                // }

                const localList = await fetch(`${process.env.PUBLIC_URL}/mapData/newDataFileList.json`)
                    .then((r) => (r.ok ? r.json() : []))
                    .catch(() => []);
                setDataFileList(Array.isArray(localList) ? localList : []);
            } catch (_) {
                setDataFileList([]);
            } finally {
                setIsDataLoading(false);
            }
        };
        loadFileList();
    }, []);

    const initMap = () => {
        if (!mapRef.current) {
            return;
        }
        const map = new window.google.maps.Map(mapRef.current, {
            center: { lat: 37.5165, lng: 126.9780 },    
            zoom: 13,
            mapId: "DEMO_MAP_ID",
            disableDefaultUI: true,
        });
        setMapInstance(map);
        // 초기 로딩은 시도 경계만 로드 (로컬 JSON)
        const initialBoundary = boundaryOptions.find(opt => opt.id === "sido");
        if (initialBoundary?.url) {
            renderPolygons(initialBoundary.url, initialBoundary.id);
        }
    };

    // Map copy.js의 공간정보 데이터 렌더링 로직 - 디바운싱으로 최적화
    useEffect(() => {
        if (!mapInstance) return;

        let zoomChangedTimer;
        let zoomChangedListener;
        
        // 줌 변경 이벤트 리스너 추가 (줌 레벨에 따라 클러스터링 재구성)
        zoomChangedListener = mapInstance.addListener('zoom_changed', () => {
            const currentZoom = mapInstance.getZoom();
            console.log('현재 줌 레벨:', currentZoom);
            
            // 줌 변경 시 데이터 재렌더링 (클러스터링 재구성)
            if (zoomChangedTimer) clearTimeout(zoomChangedTimer);
            zoomChangedTimer = setTimeout(() => {
                setAppliedSpatialDataState((prev) => ({ ...prev }));
            }, DEBOUNCE_CONFIG.ZOOM_CHANGED);
        });

        // 디바운스 타이머
        const timer = setTimeout(() => {
            // Clear previous data layers
            activeMarkerClusterers.forEach((clusterer) => clusterer.clearMarkers());
            activeHeatmaps.forEach((heatmap) => heatmap.setMap(null));
            setActiveMarkerClusterers([]);
            setActiveHeatmaps([]);

            const newMarkerClusterers = [];
            const newHeatmaps = [];

            // 적용된 상태(appliedSpatialDataState)를 기반으로 지도에 표시
            spatialInfoOptions.forEach((spatial) => {
                const state = appliedSpatialDataState[spatial.id];
                if (!state) return; // 상태가 없으면 스킵
                
                const selectedCategoryIds = Array.from(state.selected);
                
                if (selectedCategoryIds.length === 0) return;

                selectedCategoryIds.forEach((categoryId) => {
                    const category = spatial.categories.find(c => c.id === categoryId);
                    if (!category) return;
                    
                    // apiDataCache에서 직접 데이터 가져오기 (타이밍 이슈 해결)
                    const fileName = spatial.name;
                    const cachedData = apiDataCache[fileName];
                    let data = category.data || [];
                    
                    // 캐시에 데이터가 있으면 사용
                    if (cachedData && cachedData.data) {
                        const regionName = category.name;
                        if (cachedData.data[regionName]) {
                            data = cachedData.data[regionName];
                        }
                    }
                    
                    if (data.length === 0) {
                        console.log(`⚠️ 데이터 없음: ${spatial.name} - ${category.name}`);
                        return; // 데이터가 없으면 스킵
                    }
                    
                    console.log(`✅ 데이터 렌더링: ${spatial.name} - ${category.name}, ${data.length}개`)

                    if (state.mode === "POI") {
                        // POI 모드: 성능 최적화를 위한 데이터 필터링
                        const MAX_MARKERS = 10000; // 최대 마커 개수 제한
                        let dataToRender = data; // apiDataCache에서 가져온 data 사용
                        
                        console.log(`📍 POI 렌더링 시작: ${dataToRender.length}개 데이터`);
                        
                        // ⭐ 좌표 변환 및 유효성 검사
                        dataToRender = dataToRender.map(item => ({
                            ...item,
                            name: item.data_name || item.name || "이름 없음",
                            lat: parseFloat(item.lat),
                            lng: parseFloat(item.lng),
                            weight: parseFloat(item.data_value || item.weight || 0)
                        })).filter(item => {
                            const isValid = !isNaN(item.lat) && !isNaN(item.lng) &&
                                item.lat >= -90 && item.lat <= 90 &&
                                item.lng >= -180 && item.lng <= 180;
                            if (!isValid) {
                                console.warn(`⚠️ 유효하지 않은 좌표 스킵:`, item);
                            }
                            return isValid;
                        });
                        
                        console.log(`✅ 유효한 좌표: ${dataToRender.length}개`);
                        
                        if (dataToRender.length === 0) {
                            console.warn('⚠️ 표시할 유효한 POI 데이터가 없습니다.');
                            return;
                        }
                        
                        // 데이터가 너무 많으면 샘플링
                        if (dataToRender.length > MAX_MARKERS) {
                            console.log(`⚠️ 데이터가 많아 샘플링: ${dataToRender.length} → ${MAX_MARKERS}`);
                            const step = Math.ceil(dataToRender.length / MAX_MARKERS);
                            dataToRender = dataToRender.filter((_, i) => i % step === 0).slice(0, MAX_MARKERS);
                        }
                        
                        // 마커 생성
                        const markers = dataToRender.map((item) => {
                            const markerContent = document.createElement("div");
                            markerContent.className = "custom-marker";
                            
                            // name과 weight 정보를 함께 표시 (weight가 유효한 숫자일 때만)
                            const displayText = item.weight !== undefined && 
                                                item.weight !== null && 
                                                !isNaN(item.weight) && 
                                                item.weight !== ''
                                ? `${item.name} (${item.weight})` // 원본 데이터에서 가져온 데이터 이름(${item.name})과 가중치(${item.weight}) 표시 ex) 서울 (0)
                                : item.name;
                            
                            markerContent.style.cssText = `
                                background-color:rgb(72, 148, 248); color: white; padding: 4px 8px;
                                border-radius: 15px; font-size: 12px; font-weight: bold;
                                white-space: nowrap; display: inline-block;
                                box-shadow: 2px 0 5px rgba(0 ,0 ,0,0.1);
                            `;
                            markerContent.textContent = displayText;

                            return new window.google.maps.marker.AdvancedMarkerElement({
                                position: { lat: item.lat, lng: item.lng },
                                content: markerContent,
                                zIndex: 1000, // 경계 레이어보다 위에 표시되도록 높은 z-index 설정
                            });
                        });

                        // 클러스터 스타일: 원형에 숫자만 표시
                        const renderer = {
                            render: (cluster, stats, map) => {
                                const count = cluster.count;
                                const position = cluster.position;
                                
                                // 원형 클러스터 아이콘 생성
                                const clusterDiv = document.createElement('div');
                                clusterDiv.style.cssText = `
                                    width: 40px;
                                    height: 40px;
                                    background-color: #00B7C7;
                                    border-radius: 50%;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    color: #FFFFFF;
                                    font-size: 14px;
                                    font-weight: bold;
                                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                                `;
                                clusterDiv.textContent = count.toString();
                                
                                return new window.google.maps.marker.AdvancedMarkerElement({
                                    position,
                                    content: clusterDiv,
                                    zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                                });
                            },
                        };
                        
                        // 현재 줌 레벨 확인
                        const currentZoom = mapInstance.getZoom();
                        const CLUSTER_THRESHOLD_ZOOM = 15; // 이 줌 레벨 미만에서는 항상 클러스터링
                        
                        // 줌 레벨이 15보다 낮으면 모든 마커를 클러스터로 강제 표시
                        if (currentZoom < CLUSTER_THRESHOLD_ZOOM) {
                            // 데이터가 하나만 있어도 클러스터로 표시하기 위해 renderer에서 처리
                            const forceClusterRenderer = {
                                render: (cluster, stats, map) => {
                                    const count = cluster.count;
                                    const position = cluster.position;
                                    
                                    // 원형 클러스터 아이콘 생성
                                    const clusterDiv = document.createElement('div');
                                    clusterDiv.style.cssText = `
                                        width: 40px;
                                        height: 40px;
                                        background-color: #00B7C7;
                                        border-radius: 50%;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #FFFFFF;
                                        font-size: 14px;
                                        font-weight: bold;
                                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                                    `;
                                    clusterDiv.textContent = count.toString();
                                    
                                    return new window.google.maps.marker.AdvancedMarkerElement({
                                        position,
                                        content: clusterDiv,
                                        zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                                    });
                                },
                            };
                            
                            const clusterer = new MarkerClusterer({
                                map: mapInstance,
                                markers: markers,
                                gridSize: dataToRender.length > 5000 ? 400 : dataToRender.length > 2000 ? 300 : 150,
                                maxZoom: 21, // 줌 레벨이 낮으면 항상 클러스터링 유지
                                algorithmOptions: {
                                    maxZoom: 21, // 모든 줌 레벨에서 클러스터링 강제
                                    radius: 1000, // 클러스터링 반경을 매우 크게 설정하여 단일 마커도 클러스터링되도록 함
                                },
                                renderer: forceClusterRenderer,
                            });
                            newMarkerClusterers.push(clusterer);
                        } else {
                            // 줌 레벨이 15 이상이면 일반 클러스터링
                            const clusterer = new MarkerClusterer({
                                map: mapInstance,
                                markers: markers,
                                gridSize: dataToRender.length > 5000 ? 400 : dataToRender.length > 2000 ? 300 : 150,
                                maxZoom: CLUSTER_THRESHOLD_ZOOM, // 줌 레벨이 높으면 개별 마커 표시
                                algorithmOptions: {
                                    maxZoom: CLUSTER_THRESHOLD_ZOOM,
                                    radius: dataToRender.length > 5000 ? 500 : 500,
                                },
                                renderer: renderer,
                            });
                            newMarkerClusterers.push(clusterer);
                        }
                    } else if (state.mode === "Heatmap") {
                        // Heatmap 모드: 성능 최적화를 위한 데이터 샘플링
                        const MAX_HEATMAP_POINTS = 20000; // 히트맵 최대 포인트 수
                        let dataToRender = data; // apiDataCache에서 가져온 data 사용
                        
                        console.log(`🔥 Heatmap 렌더링 시작: ${dataToRender.length}개 데이터`);
                        
                        // ⭐ 좌표 변환 및 유효성 검사
                        dataToRender = dataToRender.map(item => ({
                            ...item,
                            name: item.data_name || item.name || "이름 없음",
                            lat: parseFloat(item.lat),
                            lng: parseFloat(item.lng),
                            weight: parseFloat(item.data_value || item.weight || 50)
                        })).filter(item => {
                            const isValid = !isNaN(item.lat) && !isNaN(item.lng) &&
                                item.lat >= -90 && item.lat <= 90 &&
                                item.lng >= -180 && item.lng <= 180;
                            if (!isValid) {
                                console.warn(`⚠️ 유효하지 않은 좌표 스킵:`, item);
                            }
                            return isValid;
                        });
                        
                        console.log(`✅ 유효한 좌표: ${dataToRender.length}개`);
                        
                        if (dataToRender.length === 0) {
                            console.warn('⚠️ 표시할 유효한 Heatmap 데이터가 없습니다.');
                            return;
                        }
                        
                        // 데이터가 너무 많으면 샘플링
                        if (dataToRender.length > MAX_HEATMAP_POINTS) {
                            console.log(`⚠️ 데이터가 많아 샘플링: ${dataToRender.length} → ${MAX_HEATMAP_POINTS}`);
                            const step = Math.ceil(dataToRender.length / MAX_HEATMAP_POINTS);
                            dataToRender = dataToRender.filter((_, i) => i % step === 0).slice(0, MAX_HEATMAP_POINTS);
                        }
                        
                        const heatmapData = dataToRender.map((p) => ({
                            location: new window.google.maps.LatLng(p.lat, p.lng),
                            weight: p.weight || 50,
                        }));
                        const newHeatmap =
                            new window.google.maps.visualization.HeatmapLayer({
                                data: heatmapData,
                                map: mapInstance,
                                radius: spatial.radius || 20,
                                // HeatmapLayer는 자동으로 DataLayer보다 위에 렌더링됨
                                // 명시적으로 opacity를 설정하여 경계 위에 잘 보이도록 함
                            });
                        newHeatmap.set("opacity", 0.7); // 경계 위에 잘 보이도록 opacity 조정
                        newHeatmaps.push(newHeatmap);
                    }
                });
            });

            setActiveMarkerClusterers(newMarkerClusterers);
            setActiveHeatmaps(newHeatmaps);
        }, DEBOUNCE_CONFIG.DATA_RENDERING); // 데이터 렌더링 디바운스 (성능 최적화)

        return () => {
            clearTimeout(timer);
            if (zoomChangedTimer) clearTimeout(zoomChangedTimer);
            if (zoomChangedListener) {
                window.google.maps.event.removeListener(zoomChangedListener);
            }
        };
    }, [mapInstance, appliedSpatialDataState, spatialInfoOptions, apiDataCache]);

    /**
     * 로컬 public/mapData에서 지도 데이터 가져오기
     * @param {string} fileName - 파일명(확장자 제외, 예: "골프장")
     */
    const fetchDataFromApi = async (fileName) => {
        // 서버 연동 방식(보존)
        // if (!mapInstance) return null;
        // const tableName = getTableNameFromFileNameWithoutExt(fileName);
        // if (!tableName) {
        //     console.warn(`테이블명을 찾을 수 없습니다: ${fileName}`);
        //     return null;
        // }

        // 캐시 확인
        const cached = apiDataCache[fileName];
        if (cached && cached.data) {
            console.log(`✅ 캐시된 데이터 사용: ${fileName} (${Object.keys(cached.data).length}개 지역)`);
            return cached.data;
        }

        try {
            // 서버 연동 방식(보존)
            // const koreaFullBounds = { minLng: 124.0, minLat: 33.0, maxLng: 132.0, maxLat: 39.0 };
            // const apiData = await fetchMapDataInBounds({ ...koreaFullBounds, tableName });
            // const isCsvTable = tableName.startsWith('nd_') || tableName.startsWith('bus_');
            // const transformedData = transformApiDataToFrontendFormat(apiData, isCsvTable);

            const localCsvText = await fetch(`${process.env.PUBLIC_URL}/mapData/newData/${fileName}.csv`)
                .then((r) => (r.ok ? r.text() : null))
                .catch(() => null);

            if (!localCsvText) {
                console.warn(`로컬 CSV를 찾을 수 없습니다: ${fileName}.csv`);
                return null;
            }

            const transformedData = parseLocalCsvToItems(localCsvText);

            // 행정구역별로 분류
            const categorizedData = categorizeDataByRegion(transformedData);
            
            console.log(`📂 분류된 지역: ${Object.keys(categorizedData).join(', ')}`);
            
            // 각 지역별 데이터 개수 확인
            Object.entries(categorizedData).forEach(([region, items]) => {
                console.log(`  - ${region}: ${items.length}개`);
            });

            // 캐시에 저장 (한국 전체 데이터)
            setApiDataCache(prev => ({
                ...prev,
                [fileName]: {
                    boundsKey: 'local_public_mapData',
                    bounds: null,
                    data: categorizedData
                }
            }));

            return categorizedData;
        } catch (error) {
            console.error(`API 데이터 로드 실패 (${fileName}):`, error);
            return null;
        }
    };

    // 서버 연동 방식(보존): bounds 변화에 따라 서버/API 재조회
    // useEffect(() => { ... }, [mapInstance, appliedSpatialDataState, spatialInfoOptions, dataFileList, apiDataCache]);
    // useEffect(() => { ... }, [mapInstance, appliedSpatialDataState, spatialInfoOptions, dataFileList]);

    const handleSearch = async (keyword, mode = "default") => {
        if (!keyword || !mapInstance) return;

        try {
            // 검색 시작 시 경로 관련 상태 초기화
            if (mode === "start" || mode === "end") {
                setRouteData(null);
                setRouteDistance(null);
                setRouteDuration(null);
                setShowDetails(false);

                if (routePolyline) {
                    routePolyline.forEach((line) => line.setMap(null));
                    setRoutePolyline(null);
                }

                activeLines.forEach((line) => line.setMap(null));
                setActiveLines([]);
                setTransitSteps([]);
                setCarSteps([]);
                setWalkSteps([]);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}/map/search-with-geo`,
                {
                    params: { keyword },
                }
            );

            if (mode === "default") {
                setSearchResults(response.data);
                activeMarkers.forEach((m) => m.setMap(null));
                setActiveMarkers([]);

                if (response.data.length > 0) {
                    const { lat, lng } = response.data[0];
                    moveTo(lat, lng);
                }
            } else {
                setDirectionMode(mode);
                setDirectionResults(response.data);

            }
        } catch (error) {
            console.error("검색 실패:", error);
            alert(`${content?.mapSearchError || "검색에 실패했습니다"}: ${error.response?.data?.message || error.message}`);
        }
    };

    const moveTo = (lat, lng) => {
        if (!mapInstance) return;

        const position = { lat, lng };
        mapInstance.setCenter(position);

        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        const pin = new window.google.maps.marker.PinElement({
            background: "#F84848",
            borderColor: "#F84848",
            glyphColor: "white",
        });
        pin.element.style.filter = "drop-shadow(0px 5px 5px rgba(0,0,0,.35))";

        const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
            position,
            map: mapInstance,
            content: pin.element,
        });

        setActiveMarkers([newMarker]);
    };

    // 길찾기 관련 함수들
    const createDirectionMarker = (lat, lng, type) => {
        const isStart = type === "출발";
        const url = isStart ? "/img/marker_start.svg" : "/img/marker_end.svg";

        const icon = {
            url,
            scaledSize: new window.google.maps.Size(40, 48),
            anchor: new window.google.maps.Point(20, 48),
        };

        return new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstance,
            icon,
            title: type,
            zIndex: isStart ? 2 : 1,
        });
    };

    const transformSteps = (steps) => {
        const result = [];

        steps.forEach((step) => {
            if (step.travel_mode === "TRANSIT" && step.transit_details) {
                const lineName =
                    step.transit_details.line_short_name ||
                    step.transit_details.line_name ||
                    "";
                const vehicle =
                    step.transit_details.vehicle_type === "SUBWAY"
                        ? "지하철"
                        : "버스";
                const departure = step.transit_details.departure_stop;
                const arrival = step.transit_details.arrival_stop;
                const stops = step.transit_details.num_stops;

                // 승차
                result.push({
                    type: "board",
                    text: `${lineName} ${vehicle} ${departure} 에서 승차`,
                });

                // 이동
                result.push({
                    type: "move",
                    text: `${stops} 정거장 이동`,
                });

                // 하차
                result.push({
                    type: "alight",
                    text: `${lineName} ${vehicle} ${arrival} 에서 하차`,
                });
            } else {
                // WALKING은 그대로
                result.push(step);
            }
        });

        return result;
    };

    // 짝수 인덱스 스텝에 다음 홀수 인덱스의 distance/duration를 병합
    const mergeTmapCarSteps = (steps = []) => {
        const merged = [];
        let i = 0;

        while (i < steps.length) {
            const a = steps[i]; // 메인(보통 짝수)
            const b = steps[i + 1]; // 거리/시간(보통 홀수)

            // a가 없으면 종료
            if (!a) break;

            // 병합 객체 사본
            const m = { ...a };

            // b가 존재하고, turnType이 비어있고, distance/duration 정보를 갖고 있으면 병합
            const bHasMetric = b && (b.distance != null || b.duration != null);
            const bLooksLikeMetricOnly =
                b && (b.turnType == null || b.turnType === undefined);

            if (bHasMetric && bLooksLikeMetricOnly) {
                if (m.distance == null && b.distance != null)
                    m.distance = b.distance;
                if (m.duration == null && b.duration != null)
                    m.duration = b.duration;
                i += 2; // a,b 둘 다 소비
            } else {
                i += 1; // a만 소비
            }

            merged.push(m);
        }

        return merged;
    };

    const handleDirectionSelect = (item) => {

        const cleanTitle = item.title.replace(/<[^>]*>/g, "");
        const { lat, lng } = item;
        const label = directionMode === "start" ? "출발" : "도착";

        if (!mapInstance) {
            console.error("mapInstance가 없습니다!");
            return;
        }

        const marker = createDirectionMarker(lat, lng, label);
        mapInstance.setCenter({ lat, lng });

        if (directionMode === "start") {
            if (startMarker) startMarker.setMap(null);
            setStartMarker(marker);
            setStartInfo({ lat, lng, title: cleanTitle });
            setStartAddress(cleanTitle);
    
        } else {
            if (endMarker) endMarker.setMap(null);
            setEndMarker(marker);
            setEndInfo({ lat, lng, title: cleanTitle });
            setEndAddress(cleanTitle);
    
        }

        setDirectionMode(null);
        setDirectionResults([]);
    };

    const setAsStart = (item) => {
        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        if (!mapInstance) return;
        const title = item.title.replace(/<[^>]*>/g, "");

        if (startMarker) startMarker.setMap(null);
        const m = createDirectionMarker(item.lat, item.lng, "출발");
        setStartMarker(m);
        setStartInfo({ lat: item.lat, lng: item.lng, title });
        setStartAddress(title);

        mapInstance.setCenter({ lat: item.lat, lng: item.lng });

        setInfoIndex(1);
        if (isMobile) {
            setMobileStage("directions");
            setIsPanelOpen(true);
        }

        if (endInfo) requestRoute(travelMode);
    };

    const setAsEnd = (item) => {
        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        if (!mapInstance) return;
        const title = item.title.replace(/<[^>]*>/g, "");

        if (endMarker) endMarker.setMap(null);
        const m = createDirectionMarker(item.lat, item.lng, "도착");
        setEndMarker(m);
        setEndInfo({ lat: item.lat, lng: item.lng, title });
        setEndAddress(title);

        mapInstance.setCenter({ lat: item.lat, lng: item.lng });

        setInfoIndex(1);
        if (isMobile) {
            setMobileStage("directions");
            setIsPanelOpen(true);
        }

        if (startInfo) requestRoute(travelMode);
    };

    const handleReverse = () => {
        setStartAddress(endAddress);
        setEndAddress(startAddress);

        const si = startInfo;
        const ei = endInfo;
        setStartInfo(ei ?? null);
        setEndInfo(si ?? null);

        if (startMarker) startMarker.setMap(null);
        if (endMarker) endMarker.setMap(null);

        if (ei) setStartMarker(createDirectionMarker(ei.lat, ei.lng, "출발"));
        if (si) setEndMarker(createDirectionMarker(si.lat, si.lng, "도착"));
    };

    const resetDirections = () => {
        const off = (x) => {
            if (!x) return;
            if (Array.isArray(x)) x.forEach(off);
            else if (typeof x.setMap === "function") x.setMap(null);
        };

        off(startMarker);
        off(endMarker);
        off(routePolyline);
        off(activeLines);
        off(activeMarkers);

        setStartInfo(null);
        setEndInfo(null);
        setStartAddress("");
        setEndAddress("");
        setDirectionMode(null);
        setDirectionResults([]);
        setRouteData(null);
        setRouteDistance(null);
        setRouteDuration(null);
        setTransitSteps([]);
        setCarSteps([]);
        setWalkSteps([]);
        setShowDetails(false);
    };

    // 탭 클릭 시 기존 결과 정리하는 함수
    const handleTabChange = (index, stageForMobile = null) => {
        // 탭 변경 전에 기존 결과 정리
        resetDirections();

        // 탭 변경
        setInfoIndex(index);

        if (isMobile && stageForMobile) {
            setMobileStage(stageForMobile);
            setIsPanelOpen(true);
        }
    };

    // 슬라이드 버튼
    const groupRef = useRef(null);
    const tabRefs = useRef({}); // { car: spanEl, walk: spanEl, transit: spanEl }

    const placeIndicatorWhenReady = (tries = 12) => {
        const group = groupRef.current;
        const key = travelMode || "car";
        const el = tabRefs.current[key];
        if (!group || !el) return;

        const visible = group.offsetWidth > 0 && el.offsetWidth > 0;
        if (!visible) {
            if (tries > 0)
                setTimeout(() => placeIndicatorWhenReady(tries - 1), 50);
            return;
        }

        const gr = group.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(group).paddingLeft) || 0;

        group.style.setProperty("--ind-x", `${er.left - gr.left - pad}px`);
        group.style.setProperty("--ind-w", `${er.width}px`);
    };

    // 최초
    useLayoutEffect(() => {
        requestAnimationFrame(() => placeIndicatorWhenReady());
    }, []);

    // 모드 변경 시
    useLayoutEffect(() => {
        placeIndicatorWhenReady();
    }, [travelMode]);

    // 패널 열릴 때/리사이즈 시
    useEffect(() => {
        const onResize = () => placeIndicatorWhenReady();
        window.addEventListener("resize", onResize);
        if (isPanelOpen) requestAnimationFrame(() => placeIndicatorWhenReady());
        return () => window.removeEventListener("resize", onResize);
    }, [isPanelOpen]);

    // 폰트 로드 후
    useEffect(() => {
        if (document.fonts?.ready)
            document.fonts.ready.then(() => placeIndicatorWhenReady());
    }, []);

    // **중요: 길찾기 탭이 다시 보일 때 재배치**
    useEffect(() => {
        const isDirectionsTab = infoIndex === 1; // 길찾기 탭 인덱스
        if (isDirectionsTab && isPanelOpen) placeIndicatorWhenReady();
    }, [infoIndex, isPanelOpen]);

    // 레이아웃 변동 요인 추가(모바일/도크)
    useEffect(() => {
        placeIndicatorWhenReady();
    }, [isMobile, dockOpen]);

    const requestRoute = async (mode) => {
        if (!startInfo || !endInfo) {
            alert(content?.mapSelectStartEnd || "출발지와 도착지를 모두 입력해주세요.");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}/map/directions`,
                {
                    start: { lat: startInfo.lat, lng: startInfo.lng },
                    end: { lat: endInfo.lat, lng: endInfo.lng },
                    mode: mode,
                }
            );



            const routes = response.data;
            if (routes.length > 0) {
                const route = routes[0];

                
                // 경로 데이터 설정
                setRouteData(route);
                
                // 거리와 시간 설정
                if (route.distance) {
                    setRouteDistance(route.distance);
                }
                if (route.duration) {
                    setRouteDuration(route.duration);
                }
                
                // 경로 단계 설정
                if (route.steps) {
                    if (mode === "car") {
                        setCarSteps(route.steps);
                    } else if (mode === "walk") {
                        setWalkSteps(route.steps);
                    } else if (mode === "transit") {
                        setTransitSteps(route.steps);
                    }
                }
                
                setShowDetails(true); // 경로가 성공하면 상세정보 보이기
                if (isMobile) {
                    setIsPanelOpen(false);
                }
            } else {
                setRouteData(null);
                setRouteDistance(null);
                setRouteDuration(null);
                setCarSteps([]);
                setWalkSteps([]);
                setTransitSteps([]);
                alert(content?.mapRouteNotFound || "경로를 찾을 수 없습니다.");
            }
        } catch (err) {
            console.error("경로 요청 실패:", err);
            console.error("에러 세부사항:", err.response?.data);
            alert(`${content?.mapRouteError || "경로를 찾는 데 실패했습니다"}: ${err.response?.data?.message || err.message}`);
        }
    };

    // 1) turnType → 아이콘/라벨 매핑
    const TURN_META = {
        200: { icon: "/img/icon_map_start.png", label: "출발" },
        201: { icon: "/img/icon_map_start.png", label: "도착" },

        12: { icon: "/img/icon_map_turn_left.png", label: "좌회전" },
        13: { icon: "/img/icon_map_turn_right.png", label: "우회전" },
        14: { icon: "/img/icon_map_u_turn.png", label: "유턴" },

        // 확장 매핑(선택): 문서/테스트 케이스에 맞게 조정
        16: { icon: "/img/icon_map_u_turn.png", label: "P턴" },
        17: { icon: "/img/icon_map_turn_left.png", label: "좌측 급회전" },
        18: { icon: "/img/icon_map_turn_right.png", label: "우측 급회전" },
        19: { icon: "/img/icon_map_turn_left.png", label: "좌측 진입" },
        20: { icon: "/img/icon_map_turn_right.png", label: "우측 진입" },
        21: { icon: "/img/icon_map_turn_left.png", label: "좌측 합류" },
        22: { icon: "/img/icon_map_turn_right.png", label: "우측 합류" },
        211: { icon: "/img/icon_map_via.png", label: "경유지 도착" },
        212: { icon: "/img/icon_map_via.png", label: "경유지 출발" },
    };
    const getTurnMeta = (code) =>
        TURN_META[code] ?? {
            icon: "/img/icon_map_straight.png",
            label: "안내",
        };



   // 렌더링 헬퍼 함수
   const renderStep = (step, index) => {
    if (step.travel_mode === "WALKING") {
        return (
            <li key={index} className="transit-detailed-route-walking">
                <img src="/img/icon_map_walk.png" alt="도보" />
                <div className="transit-detailed-route-info">
                    <p
                        dangerouslySetInnerHTML={{
                            __html: step.html_instructions,
                        }}
                    />
                    <span>
                        ({(step.distance / 1000).toFixed(2)}km,{" "}
                        {Math.round(step.duration / 60)}분)
                    </span>
                </div>
            </li>
        );
    }

    if (["board", "move", "alight"].includes(step.type)) {
        const isSubway = step.vehicle === "지하철";
        let iconMap = {};
        if (isSubway) {
            iconMap.move = null;
            iconMap.board = "/img/icon_map_subway.png";
            iconMap.alight = "/img/icon_map_subway.png";
        } else {
            iconMap.move = null;
            iconMap.board = "/img/icon_map_bus.png";
            iconMap.alight = "/img/icon_map_bus.png";
        }

        return (
            <li
                key={index}
                className={`transit-detailed-route-subway ${
                    step.type === "move" ? "move" : ""
                } ${
                    step.type === "move" && isSubway ? "move-subway" : ""
                } ${step.type === "move" && !isSubway ? "move-bus" : ""}`}
            >
                <div className="public-transportation-info">
                    {iconMap[step.type] && (
                        <img src={iconMap[step.type]} alt="대중교통" />
                    )}
                    <div className="public-transportation-text">
                        <div className="public-transportation-result">
                            <span>{step.text}</span>
                        </div>
                        {step.distance && (
                            <span>
                                ({(step.distance / 1000).toFixed(2)}km,{" "}
                                {Math.round(step.duration / 60)}분)
                            </span>
                        )}
                    </div>
                </div>
            </li>
        );
    }

    if (step.travel_mode === "TRANSIT") {
        const isSubway = step.transit_details?.vehicle_type === "SUBWAY";
        return (
            <li key={index} className="transit-detailed-route">
                <div className="public-transportation-info">
                    <img
                        src={
                            isSubway
                                ? "/img/icon_map_subway.png"
                                : "/img/icon_map_bus.png"
                        }
                        alt={isSubway ? "지하철" : "버스"}
                    />
                    <div className="public-transportation-text">
                        <div>
                            <p>
                                {step.transit_details?.line_short_name ||
                                    step.transit_details?.line_name ||
                                    "대중교통"}
                            </p>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: step.html_instructions,
                                }}
                            />
                        </div>
                        <span>
                            ({(step.distance / 1000).toFixed(2)}km,{" "}
                            {Math.round(step.duration / 60)}분)
                        </span>
                    </div>
                </div>
            </li>
        );
    }

    return null;
};

    // 로컬 TopoJSON URL 로 경계 로드
    const BOUNDARY_CHUNK_SIZE = 400;
    const renderPolygons = async (tableNameOrUrl, boundaryId) => {
        try {
            // 서버 연동 방식(보존)
            // if (tableNameOrUrl && typeof tableNameOrUrl === "string" && tableNameOrUrl.startsWith("kr_")) {
            //     const bounds = mapInstance?.getBounds();
            //     const geojson = await fetchBoundaryGeoJson(tableNameOrUrl, bounds);
            //     if (geojson && geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) {
            //         setGeoJsonData((prev) => ({
            //             ...prev,
            //             [boundaryId]: { type: "FeatureCollection", features: geojson.features },
            //         }));
            //     }
            //     return geojson;
            // }

            const response = await axios.get(tableNameOrUrl);
            const topoData = response.data;
            const objectKey = Object.keys(topoData.objects)[0];
            const geometries = topoData.objects[objectKey].geometries || [];
            const features = [];
            const processChunk = (start) => {
                const end = Math.min(start + BOUNDARY_CHUNK_SIZE, geometries.length);
                for (let i = start; i < end; i++) {
                    const geometry = geometries[i];
                    if (geometry) {
                        try {
                            const feat = topojson.feature(topoData, geometry);
                            if (feat) features.push(feat);
                        } catch (_) {}
                    }
                }
                if (end < geometries.length) {
                    requestAnimationFrame(() => processChunk(end));
                } else {
                    const nextFeatureCollection = { type: "FeatureCollection", features: [...features] };
                    setGeoJsonData((prev) => ({
                        ...prev,
                        [boundaryId]: nextFeatureCollection,
                    }));
                    setBoundarySourceGeoJsonData((prev) => ({
                        ...prev,
                        [boundaryId]: nextFeatureCollection,
                    }));
                }
            };
            requestAnimationFrame(() => processChunk(0));
        } catch (error) {
            console.error("Error loading polygon data:", error);
            return null;
        }
    };

    return (
        <div className="MapPage">
            <div className={`infoCon ${isPanelOpen ? "open" : "closed"}`}>
                <div className="menu">
                    {infoMenuList.map((item, index) => (
                        <div
                            key={index}
                            className={`tab tab-${item.id} ${
                                infoIndex === index ? "active" : ""
                            }`}
                            onClick={() => {
                                setInfoIndex(index);
                                if (isMobile) {
                                    if (index === 0) setMobileStage("layers");
                                    else if (index === 1) setMobileStage("directions");
                                    else setMobileStage("search");
                                }
                                setIsPanelOpen(true);
                            }}
                        >
                            {item.title}
                        </div>
                    ))}
                </div>
                <div className="info">
                    {/* 공간정보 탭 (index 0) */}
                    {infoIndex === 0 && (
                        <div className="informationWrap">
                            <div className="searchinputWrap">
                                {isMobile && isPanelOpen && (
                                    <button
                                        className="panelBackBtn"
                                        onClick={() => {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }}
                                    >
                                        ‹
                                    </button>
                                )}
                                <div className="searchInput">
                                    <input
                                        type="text"
                                        value={activeTab === (content?.mapBoundaryTab || "행정경계") ? boundarySearchKeyword : dataSearchKeyword}
                                        onChange={(e) => {
                                            if (activeTab === (content?.mapBoundaryTab || "행정경계")) {
                                                setBoundarySearchKeyword(e.target.value);
                                                searchBoundaries(e.target.value);
                                            } else {
                                                setDataSearchKeyword(e.target.value);
                                                searchData(e.target.value);
                                            }
                                        }}
                                        placeholder={content?.mapKeywordSearch || "키워드 검색"}
                                    />
                                    <button onClick={() => {
                                        if (activeTab === (content?.mapBoundaryTab || "행정경계")) {
                                            searchBoundaries(boundarySearchKeyword);
                                        } else {
                                            searchData(dataSearchKeyword);
                                        }
                                    }}>
                                        <img src="/img/icon_map_search.png" />
                                    </button>
                                </div>
                            </div>

                            <div className="informationPanel">
                                <div className="informationTabs">
                                    {[content?.mapBoundaryTab || "행정경계", content?.mapDataTab || "데이터"].map((t) => (
                                        <button
                                            key={t}
                                            className={`informationtab ${
                                                activeTab === t ? "active" : ""
                                            }`}
                                            onClick={() => {
                                                setActiveTab(t);
                                                // 탭 변경 시 검색 키워드 초기화
                                                if (t === (content?.mapBoundaryTab || "행정경계")) {
                                                    setDataSearchKeyword("");
                                                    setFilteredSpatialOptions([]);
                                                } else {
                                                    setBoundarySearchKeyword("");
                                                    setFilteredBoundaries({});
                                                }
                                            }}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>

                                {activeTab === (content?.mapBoundaryTab || "행정경계") && (
                                    <div className="tabContent">
                                        {boundaryOptions.map((boundary) => (
                                            <div key={boundary.id} className="section">
                                                <div
                                                    className="sectionHeader"
                                                    onClick={() => {
                                                        const isExpanding = !expandedBoundaries.includes(boundary.id);
                                                        setExpandedBoundaries(
                                                            (prev) => prev.includes(boundary.id) ? prev.filter((id) => id !== boundary.id) : [...prev, boundary.id]
                                                        );
                                                        if (isExpanding && (boundary.tableName || boundary.url)) {
                                                            // 서버 연동 방식(보존): 경계 목록 API 조회
                                                            // if (boundary.tableName && !boundaryListCache[boundary.id]) {
                                                            //     fetchBoundaryList(boundary.tableName)
                                                            //         .then((list) => {
                                                            //             if (Array.isArray(list)) setBoundaryListCache((prev) => ({ ...prev, [boundary.id]: list }));
                                                            //         })
                                                            //         .catch(() => {});
                                                            // }
                                                            if (!boundarySourceGeoJsonData[boundary.id]) {
                                                                renderPolygons(boundary.url || boundary.tableName, boundary.id);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span className="sectionHeaderText">
                                                        {boundary.name}
                                                    </span>
                                                    <span
                                                        className={`chev ${
                                                            expandedBoundaries.includes(boundary.id)
                                                                ? "open"
                                                                : ""
                                                        }`}
                                                    >
                                                        ▾
                                                    </span>
                                                </div>
                                                {expandedBoundaries.includes(boundary.id) && (
                                                    <div className="informationgGrid">
                                                        {(() => {
                                                            const rawList = (boundarySearchKeyword ? filteredBoundaries[boundary.id] : (boundaryListCache[boundary.id] || regionList[boundary.id])) || [];
                                                            const fullList = [...new window.Map(rawList.map(r => [r.id, r])).values()];
                                                            if (fullList.length === 0 && !boundaryListCache[boundary.id] && (boundary.tableName || boundary.url)) {
                                                                return <div style={{ padding: '12px', color: '#666', fontSize: '14px' }}>로딩 중...</div>;
                                                            }
                                                            
                                                            return (
                                                                <BoundarySectionContent
                                                                    fullList={fullList}
                                                                    boundary={boundary}
                                                                    boundaryCheckedSet={boundaryCheckedSet}
                                                                    handleBoundaryToggle={handleBoundaryToggle}
                                                                    boundarySearchKeyword={boundarySearchKeyword}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {activeTab === (content?.mapDataTab || "데이터") && (
                                    <div className="tabContent">
                                        {(dataSearchKeyword ? filteredSpatialOptions : spatialInfoOptions).map((spatial) => {
                                                const st = spatialDataState[spatial.id] || { 
                                                    mode: spatial.type === "marker" ? "POI" : "Heatmap", 
                                                    selected: new Set() 
                                                };
                                                
                                                return (
                                                <div key={spatial.id} className="section">
                                                    <div
                                                        className="sectionHeader"
                                                        onClick={() => toggleOpenSpatialData(spatial.id)}
                                                    >
                                                        <div className="sectionHeaderText">
                                                            <div className="dataHeaderText">
                                                                <span>{spatial.name}</span>
                                                                <div className="selectmap">
                                                                    <label className="radio">
                                                                        <input
                                                                            type="radio"
                                                                            name={`map-${spatial.id}`}
                                                                            checked={st.mode === "POI"}
                                                                            onChange={() =>
                                                                                setSpatialMode(spatial.id, "POI")
                                                                            }
                                                                        />
                                                                        <span>POI</span>
                                                                    </label>
                                                                    <label className="radio">
                                                                        <input
                                                                            type="radio"
                                                                            name={`map-${spatial.id}`}
                                                                            checked={st.mode === "Heatmap"}
                                                                            onChange={() =>
                                                                                setSpatialMode(spatial.id, "Heatmap")
                                                                            }
                                                                        />
                                                                        <span>Heatmap</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={`chev ${
                                                                    openSpatialData.has(spatial.id)
                                                                        ? "open"
                                                                        : ""
                                                                }`}
                                                            >
                                                                ▾
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {openSpatialData.has(spatial.id) && (
                                                        <div className="dataBody">
                                                            <div className="informationgGrid">
                                                                {/* 전체 선택 */}
                                                                {spatial.categories && spatial.categories.length > 0 && (
                                                                    <label className="informationCheck">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={spatialAllState(spatial.id).all}
                                                                            ref={(el) =>
                                                                                el &&
                                                                                (el.indeterminate =
                                                                                    spatialAllState(spatial.id).some)
                                                                            }
                                                                            onChange={(e) =>
                                                                                toggleAllSpatialCategories(
                                                                                    spatial.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                        <span>{content?.mapSpatialDataAll || "전체"}</span>
                                                                    </label>
                                                                )}

                                                                {/* 개별 카테고리들 */}
                                                                {spatial.categories.map((category) => (
                                                                    <label
                                                                        key={category.id}
                                                                        className="informationCheck"
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={st.selected.has(category.id)}
                                                                            onChange={(e) =>
                                                                                toggleSpatialCategory(
                                                                                    spatial.id,
                                                                                    category.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                        <span>{category.name}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {isDataLoading && (
                                            <div style={{ padding: '20px', textAlign: 'center', borderTop: '1px solid #e0e0e0', marginTop: '10px' }}>
                                                <div style={{ fontSize: '14px', color: '#666' }}>
                                                    데이터 로딩중...
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 하단 버튼 */}
                            <div className="informationFooter">
                                <button 
                                    className="clearBtn" 
                                    onClick={() => {
                                        // setTimeout으로 UI 반응성 유지 (버튼 클릭 애니메이션 먼저 표시)
                                        setTimeout(() => {
                                            // 경계 체크박스 모두 해제
                                            setBoundaryCheckedSet(new Set());
                                            // 적용된 경계도 초기화 (지도에서 폴리곤 제거)
                                            setSelectedRegions({});
                                            setGeoJsonData({});
                                            // 공간정보 데이터도 모두 초기화
                                            setSpatialDataState(() =>
                                                Object.fromEntries(
                                                    spatialInfoOptions.map((s) => [
                                                        s.id,
                                                        { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
                                                    ])
                                                )
                                            );
                                            // 적용된 상태도 초기화
                                            setAppliedSpatialDataState(() =>
                                                Object.fromEntries(
                                                    spatialInfoOptions.map((s) => [
                                                        s.id,
                                                        { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
                                                    ])
                                                )
                                            );
                                        }, 0);
                                    }}
                                >
                                    <img src="/img/icon_map_clearbtn.png" alt="clear" />
                                    <span>{content?.mapClearAll || "전체 해제"}</span>
                                </button>
                                <button 
                                    className="applyBtn"
                                    onClick={() => {
                                        // 클릭 핸들러를 즉시 반환하여 "응답 없는 페이지" 방지
                                        const boundarySelections = {};
                                        boundaryCheckedSet.forEach((key) => {
                                            if (key.startsWith('boundary:')) {
                                                const parts = key.split(':');
                                                const boundaryId = parts[1];
                                                const regionId = parts.length > 2 ? parts.slice(2).join(':') : '';
                                                if (boundaryId && regionId) {
                                                    if (!boundarySelections[boundaryId]) {
                                                        boundarySelections[boundaryId] = [];
                                                    }
                                                    boundarySelections[boundaryId].push(regionId);
                                                }
                                            }
                                        });
                                        
                                        const nextSpatialState = { ...spatialDataState };
                                        const nextDataFileList = dataFileList;
                                        const nextSpatialInfoOptions = spatialInfoOptions;
                                        const mapInst = mapInstance;
                                        
                                        if (isMobile) {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }
                                        
                                        // 상태 업데이트와 API 호출을 다음 프레임으로 미뤄 메인 스레드 블로킹 방지
                                        requestAnimationFrame(async () => {
                                            console.log('🔵 적용하기 시작');
                                            console.log('boundarySelections:', boundarySelections);
                                            console.log('nextSpatialState:', nextSpatialState);
                                            
                                            // 체크된 경계들의 GeoJSON 로드 (ID 기반, 화면 밖도 OK)
                                            // 여러 경계를 한 번에 setGeoJsonData로 반영해야 배칭으로 덮어쓰임 방지
                                            const boundaryLoadPromises = [];
                                            for (const [boundaryId, regionIds] of Object.entries(boundarySelections)) {
                                                const boundary = boundaryOptions.find(b => b.id === boundaryId);
                                                if (boundary && regionIds.length > 0) {
                                                    console.log(`🌍 경계 GeoJSON 로드: ${boundary.name} (${regionIds.length}개 ID)`);
                                                    boundaryLoadPromises.push(
                                                        Promise.resolve().then(async () => {
                                                            // 서버 연동 방식(보존): ID 기반 서버 GeoJSON 조회
                                                            // const geojson = await fetchBoundaryGeoJson(boundary.tableName, null, regionIds);

                                                            let sourceGeo = boundarySourceGeoJsonData[boundaryId] || geoJsonData[boundaryId];
                                                            if (!sourceGeo?.features?.length && boundary.url) {
                                                                const response = await axios.get(boundary.url);
                                                                const topoData = response.data;
                                                                const objectKey = Object.keys(topoData.objects)[0];
                                                                const geometries = topoData.objects[objectKey]?.geometries || [];
                                                                const features = geometries.map((geometry) => {
                                                                    try {
                                                                        return topojson.feature(topoData, geometry);
                                                                    } catch (_) {
                                                                        return null;
                                                                    }
                                                                }).filter(Boolean);
                                                                sourceGeo = { type: "FeatureCollection", features };
                                                            }
                                                            if (!sourceGeo?.features?.length) {
                                                                return { boundaryId, geojson: null };
                                                            }

                                                            const targetIdSet = new Set(regionIds.map((id) => String(id)));
                                                            const filteredFeatures = sourceGeo.features.filter((feature) => {
                                                                const featureId = getBoundaryFeatureId(feature.properties) ?? feature.id;
                                                                return featureId != null && targetIdSet.has(String(featureId));
                                                            });

                                                            const geojson = { type: "FeatureCollection", features: filteredFeatures };
                                                            console.log(`✅ 경계 로드 완료: ${boundary.name}, features: ${geojson.features.length}`);
                                                            return { boundaryId, geojson };
                                                        }).catch(err => {
                                                            console.error(`❌ 경계 GeoJSON 로드 실패 (${boundaryId}):`, err);
                                                            return { boundaryId, geojson: null };
                                                        })
                                                    );
                                                }
                                            }
                                            
                                            // 체크된 경계만 남기고 나머지는 geoJsonData에서 제거
                                            if (boundaryLoadPromises.length > 0) {
                                                const results = await Promise.all(boundaryLoadPromises);
                                                setGeoJsonData(() => {
                                                    const next = {};
                                                    results.forEach(({ boundaryId, geojson }) => {
                                                        if (geojson && geojson.type === 'FeatureCollection') {
                                                            next[boundaryId] = geojson;
                                                        }
                                                    });
                                                    return next;
                                                });
                                                console.log('✅ 모든 경계 로드 완료');
                                            } else {
                                                // 체크된 항목이 없으면 모두 제거
                                                setGeoJsonData({});
                                            }
                                            // selectedRegions 설정 (폴리곤 렌더링 트리거)
                                            setSelectedRegions(boundarySelections);
                                            
                                            const bounds = mapInst?.getBounds();
                                            
                                            // 데이터 로드
                                            if (!bounds || !mapInst || !nextSpatialInfoOptions?.length || !nextDataFileList?.length) {
                                                console.log('⚠️ bounds/map/options 없음');
                                                return;
                                            }
                                            
                                            const loadPromises = [];
                                            const loadedFiles = new Set();
                                            
                                            console.log('📊 데이터 로드 시작');
                                            console.log('nextSpatialState:', nextSpatialState);
                                            
                                            nextSpatialInfoOptions.forEach((spatial) => {
                                                const state = nextSpatialState[spatial.id];
                                                console.log(`  - ${spatial.name} (${spatial.id}): state.selected=`, state?.selected);
                                                if (!state || !state.selected) return;
                                                
                                                const selectedCategoryIds = Array.from(state.selected);
                                                console.log(`    selected IDs: ${selectedCategoryIds.join(', ')}`);
                                                if (selectedCategoryIds.length === 0) return;
                                                
                                                selectedCategoryIds.forEach((categoryId) => {
                                                    const category = spatial.categories.find(c => c.id === categoryId);
                                                    console.log(`      category ${categoryId}: ${category?.name}`);
                                                    if (!category) return;

                                                    const fileName = spatial.name;
                                                    console.log(`        로컬 fileName: "${fileName}"`);
                                                    if (!loadedFiles.has(fileName)) {
                                                        const fileExists = nextDataFileList.some(file => file.replace('.csv', '') === fileName);
                                                        console.log(`        fileExists: ${fileExists}, 파일 목록:`, nextDataFileList.slice(0, 5));
                                                        if (fileExists) {
                                                            loadedFiles.add(fileName);
                                                            console.log(`📁 로컬 데이터 로드 예약: ${fileName}`);
                                                            loadPromises.push(fetchDataFromApi(fileName));
                                                        }
                                                    }
                                                });
                                            });
                                            
                                            console.log(`📡 총 ${loadPromises.length}개 데이터 API 호출`);
                                            
                                            if (loadPromises.length > 0) {
                                                Promise.all(loadPromises).then(() => {
                                                    console.log('✅ 데이터 API 로드 완료, apiDataCache 업데이트됨');
                                                    // 데이터 로드 완료 후 appliedSpatialDataState 업데이트 (순서 중요!)
                                                    setTimeout(() => {
                                                        console.log('🎯 appliedSpatialDataState 최종 업데이트');
                                                        setAppliedSpatialDataState(nextSpatialState);
                                                    }, 100);
                                                }).catch((err) => {
                                                    console.error('❌ 데이터 API 호출 오류:', err);
                                                });
                                            } else {
                                                console.log('⚠️ 데이터 API 호출 없음 → 즉시 적용');
                                                setAppliedSpatialDataState(nextSpatialState);
                                            }
                                        });
                                    }}
                                >
                                    <span>{content?.mapApply || "적용하기"}</span>
                                    <img src="/img/icon_map_applybtn.png" alt="apply" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 길찾기 */}
                    {infoIndex === 1 && (
                        <div>
                            <div className="directionsSearchWrap">
                                <div className="directionsSearchTop">
                                    {isMobile && isPanelOpen && (
                                        <button
                                            className="panelBackBtn"
                                            style={{ paddingTop: "9px" }}
                                            onClick={() => {
                                                setMobileStage("home");
                                                setIsPanelOpen(false);
                                            }}
                                        >
                                            ‹
                                        </button>
                                    )}
                                    <div className="directionsSearch">
                                        <div className="originWrap">
                                            <button
                                                onClick={() =>
                                                    handleSearch(
                                                        startAddress,
                                                        "start"
                                                    )
                                                }
                                            >
                                                <img
                                                    src="/img/icon_map_origin.png"
                                                    alt="originicon"
                                                />
                                            </button>
                                            <input
                                                type="text"
                                                value={startAddress}
                                                onChange={(e) =>
                                                    setStartAddress(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={content?.mapStartPlaceholder || "출발지 입력"}
                                                onKeyDown={(e) =>
                                                    e.key === "Enter" &&
                                                    handleSearch(
                                                        startAddress,
                                                        "start"
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="searchBoxLine"></div>
                                        <div className="destinationWrap">
                                            <button
                                                onClick={() =>
                                                    handleSearch(
                                                        endAddress,
                                                        "end"
                                                    )
                                                }
                                            >
                                                <img
                                                    src="/img/icon_map_destination.png"
                                                    alt="destinationicon"
                                                />
                                            </button>
                                            <input
                                                type="text"
                                                value={endAddress}
                                                onChange={(e) =>
                                                    setEndAddress(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={content?.mapEndPlaceholder || "도착지 입력"}
                                                onKeyDown={(e) =>
                                                    e.key === "Enter" &&
                                                    handleSearch(
                                                        endAddress,
                                                        "end"
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="destinationiconBox">
                                        <button
                                            className="reverseBtn"
                                            onClick={handleReverse}
                                        >
                                            <img
                                                src="/img/icon_map_reverse.png"
                                                alt="reverseicon"
                                            />
                                        </button>

                                        <button
                                            className="resetBtn"
                                            onClick={resetDirections}
                                        >
                                            <img
                                                src="/img/icon_map_reset.png"
                                                alt="reseticon"
                                            />
                                        </button>
                                    </div>
                                </div>
                                <div className="directionsSearchBottom">
                                    {/* <div className="modeGroup" ref={groupRef}>
                                        <span
                                            className="indicator"
                                            aria-hidden="true"
                                        />
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="car"
                                                checked={travelMode === "car"}
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.car = el)
                                                }
                                            >
                                                자동차
                                            </span>
                                        </label>
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="walk"
                                                checked={travelMode === "walk"}
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.walk = el)
                                                }
                                            >
                                                도보
                                            </span>
                                        </label>
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="transit"
                                                checked={
                                                    travelMode === "transit"
                                                }
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.transit =
                                                        el)
                                                }
                                            >
                                                대중교통
                                            </span>
                                        </label>
                                    </div> */}
                                    <SlidingRadioTabs
                                        name="travel"
                                        options={[
                                            { value: "car", label: "자동차" },
                                            { value: "walk", label: "도보" },
                                            {
                                                value: "transit",
                                                label: "대중교통",
                                            },
                                        ]}
                                        value={travelMode}
                                        onChange={(v) => {
                                            setTravelMode(v);
                                            setRouteData(null);
                                        }}
                                        deps={[
                                            isPanelOpen,
                                            infoIndex,
                                            isMobile,
                                            dockOpen,
                                        ]}
                                    />
                                    <button
                                        className="routeBtn"
                                        onClick={() => requestRoute(travelMode)}
                                    >
                                        {content?.mapRouteBtn || "경로찾기"} ›
                                    </button>
                                </div>
                            </div>
                            {directionResults.length > 0 && (
                                <ul className="directionsResultsBox">
                                    {directionResults.map((item, idx) => (
                                        <li
                                            key={idx}
                                            onClick={() =>
                                                handleDirectionSelect(item)
                                            }
                                            className="directionsResults"
                                        >
                                            <div className="resultsInpoText">
                                                <h3>
                                                    {item.title.replace(
                                                        /<[^>]*>/g,
                                                        ""
                                                    )}
                                                </h3>
                                                <p>{item.roadAddress}</p>
                                            </div>
                                            <div className="resultsInpoText">
                                                <button>
                                                    {directionMode === "start"
                                                        ? (content?.mapStart || "출발")
                                                        : (content?.mapEnd || "도착")}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {routeDistance && routeDuration && (
                                <div className="route-summary">
                                    <div className="routeDurationBox">
                                        <p className="routeDurationhour">
                                            {(() => {
                                                const hours = Math.floor(
                                                    routeDuration / 3600
                                                ); // 초 → 시간
                                                const minutes = Math.floor(
                                                    (routeDuration % 3600) / 60
                                                );
                                                const seconds =
                                                    routeDuration % 60;
                                                return (
                                                    <>
                                                        {hours > 0 &&
                                                            `${hours}시간 `}
                                                        {minutes > 0 &&
                                                            `${minutes}분 `}
                                                        {seconds > 0 &&
                                                            `${seconds}초`}
                                                    </>
                                                );
                                            })()}
                                        </p>
                                        <p className="routeDurationkm">
                                            {(routeDistance / 1000).toFixed(1)}
                                            km
                                        </p>
                                    </div>
                                    <button
                                        className="detailed-route-btn"
                                        onClick={() =>
                                            setShowDetails((v) => !v)
                                        }
                                        aria-expanded={showDetails}
                                    >
                                        {content?.mapDetailRoute || "상세 경로"}
                                        <img
                                            src="/img/icon_map_detailed-route-btn.png"
                                            alt={
                                                showDetails
                                                    ? "닫힘 버튼"
                                                    : "열림 버튼"
                                            }
                                        />
                                    </button>
                                </div>
                            )}

                            {carSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>
                                        {carSteps.map((step, index) => {
                                            const { icon, label } = getTurnMeta(
                                                step.turnType || null
                                            );
                                            const desc = step.turnType
                                                ? step.description
                                                : `${step.description} 이동`;

                                            const hasDistanceInfo =
                                                typeof step.distance ===
                                                    "number" &&
                                                typeof step.duration ===
                                                    "number";

                                            return (
                                                <li
                                                    key={index}
                                                    className="car-detailed-route"
                                                >
                                                    <img
                                                        src={icon}
                                                        alt={label}
                                                    />
                                                    <div className="car-detailed-route-info">
                                                        <p
                                                            dangerouslySetInnerHTML={{
                                                                __html: desc,
                                                            }}
                                                        />

                                                        {hasDistanceInfo && (
                                                            <span>
                                                                (
                                                                {(
                                                                    step.distance /
                                                                    1000
                                                                ).toFixed(2)}
                                                                km,{" "}
                                                                {Math.round(
                                                                    step.duration /
                                                                        60
                                                                )}
                                                                분)
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}

                            {walkSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>
                                        {walkSteps.map((step, index) => {
                                            const desc = step.turnType
                                                ? step.description
                                                : `${step.description} 이동`;

                                            return (
                                                <li
                                                    key={index}
                                                    className="walk-detailed-route"
                                                >
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: desc,
                                                        }}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}

                            {transitSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>{transitSteps.map(renderStep)}</ol>
                                </div>
                            )}
                        </div>
                    )}
                    {/* 위치검색 탭 (index 2) */}
                    {infoIndex === 2 && (
                        <div>
                            <div className="searchinputWrap">
                                {isMobile && isPanelOpen && (
                                    <button
                                        className="panelBackBtn"
                                        onClick={() => {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }}
                                    >
                                        ‹
                                    </button>
                                )}
                                <div className="searchInput">
                                    <input
                                        type="text"
                                        value={searchAddress}
                                        onChange={(e) =>
                                            setSearchAddress(e.target.value)
                                        }
                                        placeholder={content?.mapSearchPlaceholder || "주소 검색"}
                                    />
                                    <button
                                        onClick={() =>
                                            handleSearch(searchAddress, "default")
                                        }
                                    >
                                        <img src="/img/icon_map_search.png" />
                                    </button>
                                </div>
                            </div>
                            <div className="searchResultsWrap">
                                <ul>
                                    {searchResults.map((item, idx) => (
                                        <li
                                            key={idx}
                                            onClick={(e) => {
                                                if (e.target.closest("button")) return;
                                                moveTo(item.lat, item.lng);
                                                if (isMobile) {
                                                    setMobileStage("home");
                                                    setIsPanelOpen(false);
                                                }

                                            }}
                                            className="searchResults"
                                        >
                                            <h3>
                                                {item.title.replace(/<[^>]*>/g, "")}
                                            </h3>
                                            <p>{item.roadAddress}</p>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAsStart(item);
                                                    }}
                                                >
                                                    {content?.mapStart || "출발"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAsEnd(item);
                                                    }}
                                                >
                                                    {content?.mapEnd || "도착"}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
                <button
                    className="panelToggleBtn"
                    onClick={() => setIsPanelOpen((v) => !v)}
                >
                </button>
            </div>

            <div className="mapContainer">
                <div ref={mapRef} className="map" style={{ width: "100%" }} />
            </div>
            
            {/* 모바일 하단 버튼 */}
            {isMobile && !isPanelOpen && (
                <div className={`mobileDock ${dockOpen ? "open" : "closed"}`}>
                    <button
                        className="dockHandle"
                        onClick={() => setDockOpen((v) => !v)}
                        aria-label={dockOpen ? "도크 닫기" : "도크 열기"}
                    >
                        {dockOpen ? "▾" : "▴"}
                    </button>
                    {dockOpen && (
                        <div>
                            <div className="dockSearch">
                                <input
                                    type="text"
                                    value={searchAddress}
                                    onChange={(e) => setSearchAddress(e.target.value)}
                                    onClick={() => {
                                        setInfoIndex(2);
                                        setMobileStage("search");
                                        setIsPanelOpen(true);
                                    }}
                                    placeholder="주소 검색"
                                />
                            </div>
                            <div className="menu menu--mobile">
                                {infoMenuList.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={`tab tab-${item.id} ${
                                            infoIndex === index ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setInfoIndex(index);
                                            if (index === 0) {
                                                setMobileStage("layers");
                                            } else if (index === 1) {
                                                setMobileStage("directions");
                                            } else {
                                                setMobileStage("search");
                                            }
                                            setIsPanelOpen(true);
                                        }}
                                    >
                                        {item.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Map;
